import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { HomeOutlined, ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Layout, Row, Col, Button, Space, Modal, Alert, Spin } from "antd";

export default function FetchCSVData() {
    const [csvData, setCsvData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        fetchCSVData();
    }, []);

    useEffect(() => {
        applyFilter();
    }, [searchTerm, csvData]);

    useEffect(() => {
        if (sortConfig.key) {
            sortData(sortConfig.key, sortConfig.direction);
        }
    }, [sortConfig]);

    const fetchCSVData = async () => {
        const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSX1Caoo3ikmlja3tPwugCG5Tj6A8uwfZ0nP2tjWwkQEhkLECauJ-y_VmCLKxnBGw/pub?gid=59885557&single=true&output=csv';
        
        setLoading(true);
        setError(null);
        
        try {
            console.log('Fetching from:', csvUrl);
            
            // Try different approaches to handle CORS
            const response = await fetch(csvUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'text/csv,text/plain,*/*',
                },
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
            }

            const csvText = await response.text();
            console.log('CSV Response received, length:', csvText.length);
            console.log('First 200 characters:', csvText.substring(0, 200));

            if (!csvText || csvText.trim().length === 0) {
                throw new Error('Empty CSV response received');
            }

            const parsedCsvData = parseCSV(csvText);
            console.log('Parsed data:', parsedCsvData);
            
            if (parsedCsvData.length === 0) {
                throw new Error('No data found in CSV or parsing failed');
            }

            setCsvData(parsedCsvData);
            setFilteredData(parsedCsvData);
            
        } catch (err) {
            console.error('Error fetching CSV data:', err);
            setError(err.message);
            
            // Fallback: try with axios if available
            if (window.axios) {
                try {
                    console.log('Trying fallback with axios...');
                    const axiosResponse = await window.axios.get(csvUrl);
                    const parsedCsvData = parseCSV(axiosResponse.data);
                    setCsvData(parsedCsvData);
                    setFilteredData(parsedCsvData);
                    setError(null);
                } catch (axiosErr) {
                    console.error('Axios fallback also failed:', axiosErr);
                    setError(`Both fetch and axios failed. Last error: ${axiosErr.message}`);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const parseCSV = (csvText) => {
        try {
            const rows = csvText.trim().split(/\r?\n/);
            console.log('Total rows found:', rows.length);
            
            if (rows.length < 2) {
                throw new Error('CSV must have at least header and one data row');
            }

            // More robust CSV parsing
            const parseRow = (row) => {
                const result = [];
                let current = '';
                let inQuotes = false;
                
                for (let i = 0; i < row.length; i++) {
                    const char = row[i];
                    
                    if (char === '"') {
                        inQuotes = !inQuotes;
                    } else if (char === ',' && !inQuotes) {
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += char;
                    }
                }
                result.push(current.trim());
                return result;
            };

            const headers = parseRow(rows[0]).map(header => header.replace(/^"|"$/g, '').trim());
            console.log('Headers found:', headers);
            
            const data = [];
            
            for (let i = 1; i < rows.length; i++) {
                const rowData = parseRow(rows[i]);
                
                if (rowData.length === 1 && rowData[0] === '') {
                    continue; // Skip empty rows
                }
                
                const rowObject = {};
                for (let j = 0; j < headers.length; j++) {
                    const value = j < rowData.length ? rowData[j].replace(/^"|"$/g, '').trim() : '';
                    rowObject[headers[j]] = value;
                }
                data.push(rowObject);
            }
            
            console.log('Parsed data sample:', data.slice(0, 3));
            return data;
            
        } catch (parseError) {
            console.error('CSV parsing error:', parseError);
            throw new Error(`CSV parsing failed: ${parseError.message}`);
        }
    };

    const applyFilter = () => {
        if (!searchTerm) {
            setFilteredData(csvData);
            return;
        }
        
        const lowercasedFilter = searchTerm.toLowerCase();
        const filtered = csvData.filter((row) =>
            Object.values(row).some((value) => 
                value && value.toString().toLowerCase().includes(lowercasedFilter)
            )
        );
        setFilteredData(filtered);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortData = (key, direction) => {
        const sorted = [...filteredData].sort((a, b) => {
            const valA = a[key] ? a[key].toString().toLowerCase() : '';
            const valB = b[key] ? b[key].toString().toLowerCase() : '';

            // Check if both values are numbers
            const numA = parseFloat(valA);
            const numB = parseFloat(valB);
            
            if (!isNaN(numA) && !isNaN(numB)) {
                return direction === 'asc' ? numA - numB : numB - numA;
            }

            return direction === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        });

        setFilteredData(sorted);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '⇅';
        return sortConfig.direction === 'asc' ? '⬆' : '⬇';
    };

    return (
        <div style={{ padding: '20px' }}>
            <Row gutter={[16, 16]}>
                <Col span={20}>
                    <h3>ExportERP - Task Management</h3>
                </Col>
                <Col span={4}>
                    <Space>
                        <Button 
                            type="primary" 
                            icon={<ReloadOutlined />}
                            onClick={fetchCSVData}
                            loading={loading}
                        >
                            Reload
                        </Button>
                        <Link to="/dashboard">
                            <Button type="danger" icon={<HomeOutlined />}>
                                Dashboard
                            </Button>
                        </Link>
                    </Space>
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    {error && (
                        <Alert
                            message="Error Loading Data"
                            description={
                                <div>
                                    <p><strong>Error:</strong> {error}</p>
                                    <p><strong>Troubleshooting tips:</strong></p>
                                    <ul>
                                        <li>Check if the Google Sheet is publicly accessible</li>
                                        <li>Verify the sheet URL is correct</li>
                                        <li>Check browser console for CORS errors</li>
                                        <li>Try refreshing the page</li>
                                    </ul>
                                </div>
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: '16px' }}
                            action={
                                <Button size="small" onClick={fetchCSVData}>
                                    Retry
                                </Button>
                            }
                        />
                    )}

                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                marginBottom: '15px',
                                padding: '8px 12px',
                                width: '300px',
                                fontSize: '16px',
                                borderRadius: '6px',
                                border: '1px solid #d9d9d9',
                                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)'
                            }}
                        />

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Spin size="large" />
                                <p style={{ marginTop: '16px' }}>Loading Google Sheet data...</p>
                            </div>
                        ) : filteredData.length > 0 ? (
                            <div>
                                <p style={{ marginBottom: '10px', color: '#666' }}>
                                    Showing {filteredData.length} of {csvData.length} records
                                </p>
                                <div style={{ overflowX: 'auto' }}>
                                    <table 
                                        style={{ 
                                            width: '100%', 
                                            borderCollapse: 'collapse',
                                            border: '1px solid #e8e8e8',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <thead style={{ backgroundColor: '#fafafa' }}>
                                            <tr>
                                                {Object.keys(filteredData[0]).map((header, index) => (
                                                    <th 
                                                        key={index} 
                                                        onClick={() => handleSort(header)} 
                                                        style={{ 
                                                            cursor: 'pointer', 
                                                            textAlign: 'left',  
                                                            padding: '12px',
                                                            borderRight: '1px solid #e8e8e8',
                                                            fontWeight: '600',
                                                            userSelect: 'none',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = '#fafafa'}
                                                    >
                                                        {header} <span style={{ color: '#666' }}>{getSortIcon(header)}</span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredData.map((row, rowIndex) => (
                                                <tr 
                                                    key={rowIndex}
                                                    style={{ 
                                                        borderBottom: '1px solid #f0f0f0',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f9f9f9'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                >
                                                    {Object.values(row).map((value, colIndex) => (
                                                        <td 
                                                            key={colIndex}
                                                            style={{ 
                                                                textAlign: 'left', 
                                                                padding: '10px 12px',
                                                                borderRight: '1px solid #f0f0f0'
                                                            }}
                                                        >
                                                            {value || '-'}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            !loading && !error && (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <ExclamationCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                    <p>No data found. The CSV might be empty or not accessible.</p>
                                </div>
                            )
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
}